// Portal i18n dictionaries. Keys are namespaced by area: nav.*, status.*,
// btn.*, common.*, etc. English is the source of truth — missing keys in
// other locales gracefully fall back to English at render time.
//
// Keep translations natural (not literal/machine). Reviewed for tone with
// native-speaker style guides for hospitality / construction contexts.

export const LANGS = [
  { code: "en", label: "English",   nativeLabel: "English",  dir: "ltr" },
  { code: "ko", label: "Korean",    nativeLabel: "한국어",     dir: "ltr" },
  { code: "ar", label: "Arabic",    nativeLabel: "العربية",   dir: "rtl" },
  { code: "zh", label: "Mandarin",  nativeLabel: "中文",       dir: "ltr" },
];

export const LANG_CODES = LANGS.map((l) => l.code);

const en = {
  // Navigation
  "nav.dashboard": "Dashboard",
  "nav.projects": "Projects",
  "nav.team": "Team & Access",
  "nav.profile": "Profile",
  "nav.logout": "Logout",
  "nav.changePassword": "Change password",
  // Tabs
  "tab.overview": "Overview",
  "tab.timeline": "Timeline / Gantt",
  "tab.milestones": "Milestones",
  "tab.approvals": "Approvals",
  "tab.updates": "Daily Updates",
  "tab.photos": "Photos",
  "tab.reports": "Reports",
  "tab.financials": "Financials",
  "tab.files": "Files",
  // Status
  "status.planning": "Planning",
  "status.active": "Active",
  "status.delayed": "Delayed",
  "status.atRisk": "At Risk",
  "status.completed": "Completed",
  "status.handover": "Handover",
  "status.awaitingApproval": "Awaiting Approval",
  "status.reportsPending": "Reports Pending",
  "status.draft": "Draft",
  "status.sent": "Sent",
  "status.paid": "Paid",
  "status.overdue": "Overdue",
  // Buttons
  "btn.uploadPhotos": "Upload Photos",
  "btn.addDailyUpdate": "Add Daily Update",
  "btn.generateReport": "Generate Report",
  "btn.sendReport": "Send Report",
  "btn.reply": "Reply",
  "btn.viewReport": "View Report",
  "btn.download": "Download",
  "btn.view": "View",
  "btn.save": "Save",
  "btn.cancel": "Cancel",
  "btn.delete": "Delete",
  "btn.discard": "Discard",
  "btn.approveSend": "Approve & Send",
  "btn.approveOnly": "Approve only",
  "btn.editDraft": "Edit Draft",
  "btn.upload": "Upload",
  "btn.markFinal": "Mark as Final",
  // Common
  "common.status": "Status",
  "common.progress": "Progress",
  "common.milestone": "Milestone",
  "common.delayed": "Delayed",
  "common.completed": "Completed",
  "common.active": "Active",
  "common.planning": "Planning",
  "common.handover": "Handover",
  "common.recipients": "Recipients",
  "common.audience": "Audience",
  "common.internal": "Internal",
  "common.client": "Client",
  "common.dueDate": "Due Date",
  "common.uploadedBy": "Uploaded by",
  "common.uploadedAt": "Uploaded at",
  "common.lastUpdated": "Last updated",
  "common.daysRemaining": "Days remaining",
  "common.overdue": "Overdue",
  "common.language": "Language",
  "common.preferredLanguage": "Preferred language",
  // Report headings
  "report.projectName": "Project Name",
  "report.reportDate": "Report Date",
  "report.workCompleted": "Work Completed",
  "report.photos": "Photos",
  "report.delays": "Delays / Issues",
  "report.nextDayPlan": "Next Day Plan",
  "report.pendingApprovals": "Pending Approvals",
  "report.comments": "Comments",
  "report.viewFull": "View Full Report",
  "report.reportLanguage": "Report language",
  "report.sameAsRecipient": "Same as recipient's preferred language",
};

const ko = {
  "nav.dashboard": "대시보드",
  "nav.projects": "프로젝트",
  "nav.team": "팀 및 접근 권한",
  "nav.profile": "프로필",
  "nav.logout": "로그아웃",
  "nav.changePassword": "비밀번호 변경",
  "tab.overview": "개요",
  "tab.timeline": "일정 / 간트",
  "tab.milestones": "마일스톤",
  "tab.approvals": "승인",
  "tab.updates": "일일 업데이트",
  "tab.photos": "사진",
  "tab.reports": "보고서",
  "tab.financials": "재무",
  "tab.files": "파일",
  "status.planning": "계획 중",
  "status.active": "진행 중",
  "status.delayed": "지연됨",
  "status.atRisk": "위험",
  "status.completed": "완료",
  "status.handover": "인계",
  "status.awaitingApproval": "승인 대기",
  "status.reportsPending": "보고서 대기",
  "status.draft": "초안",
  "status.sent": "발송됨",
  "status.paid": "결제 완료",
  "status.overdue": "기한 초과",
  "btn.uploadPhotos": "사진 업로드",
  "btn.addDailyUpdate": "일일 업데이트 추가",
  "btn.generateReport": "보고서 생성",
  "btn.sendReport": "보고서 발송",
  "btn.reply": "답장",
  "btn.viewReport": "보고서 보기",
  "btn.download": "다운로드",
  "btn.view": "보기",
  "btn.save": "저장",
  "btn.cancel": "취소",
  "btn.delete": "삭제",
  "btn.discard": "폐기",
  "btn.approveSend": "승인 및 발송",
  "btn.approveOnly": "승인만",
  "btn.editDraft": "초안 편집",
  "btn.upload": "업로드",
  "btn.markFinal": "최종본으로 지정",
  "common.status": "상태",
  "common.progress": "진행률",
  "common.milestone": "마일스톤",
  "common.delayed": "지연됨",
  "common.completed": "완료",
  "common.active": "진행 중",
  "common.planning": "계획 중",
  "common.handover": "인계",
  "common.recipients": "수신자",
  "common.audience": "대상",
  "common.internal": "내부",
  "common.client": "고객",
  "common.dueDate": "마감일",
  "common.uploadedBy": "업로드한 사용자",
  "common.uploadedAt": "업로드 시각",
  "common.lastUpdated": "최종 업데이트",
  "common.daysRemaining": "남은 일수",
  "common.overdue": "기한 초과",
  "common.language": "언어",
  "common.preferredLanguage": "선호 언어",
  "report.projectName": "프로젝트명",
  "report.reportDate": "보고일",
  "report.workCompleted": "완료된 작업",
  "report.photos": "사진",
  "report.delays": "지연 / 이슈",
  "report.nextDayPlan": "익일 계획",
  "report.pendingApprovals": "승인 대기",
  "report.comments": "코멘트",
  "report.viewFull": "전체 보고서 보기",
  "report.reportLanguage": "보고서 언어",
  "report.sameAsRecipient": "수신자의 선호 언어와 동일",
};

const ar = {
  "nav.dashboard": "لوحة التحكم",
  "nav.projects": "المشاريع",
  "nav.team": "الفريق والصلاحيات",
  "nav.profile": "الملف الشخصي",
  "nav.logout": "تسجيل الخروج",
  "nav.changePassword": "تغيير كلمة المرور",
  "tab.overview": "نظرة عامة",
  "tab.timeline": "الجدول الزمني / غانت",
  "tab.milestones": "المعالم",
  "tab.approvals": "الموافقات",
  "tab.updates": "التحديثات اليومية",
  "tab.photos": "الصور",
  "tab.reports": "التقارير",
  "tab.financials": "الأمور المالية",
  "tab.files": "الملفات",
  "status.planning": "قيد التخطيط",
  "status.active": "نشط",
  "status.delayed": "متأخر",
  "status.atRisk": "في خطر",
  "status.completed": "مكتمل",
  "status.handover": "تسليم",
  "status.awaitingApproval": "بانتظار الموافقة",
  "status.reportsPending": "تقارير معلقة",
  "status.draft": "مسودة",
  "status.sent": "تم الإرسال",
  "status.paid": "مدفوع",
  "status.overdue": "متأخر السداد",
  "btn.uploadPhotos": "رفع الصور",
  "btn.addDailyUpdate": "إضافة تحديث يومي",
  "btn.generateReport": "إنشاء تقرير",
  "btn.sendReport": "إرسال التقرير",
  "btn.reply": "رد",
  "btn.viewReport": "عرض التقرير",
  "btn.download": "تنزيل",
  "btn.view": "عرض",
  "btn.save": "حفظ",
  "btn.cancel": "إلغاء",
  "btn.delete": "حذف",
  "btn.discard": "تجاهل",
  "btn.approveSend": "اعتماد وإرسال",
  "btn.approveOnly": "اعتماد فقط",
  "btn.editDraft": "تعديل المسودة",
  "btn.upload": "رفع",
  "btn.markFinal": "تعيين كنسخة نهائية",
  "common.status": "الحالة",
  "common.progress": "التقدم",
  "common.milestone": "المعلم",
  "common.delayed": "متأخر",
  "common.completed": "مكتمل",
  "common.active": "نشط",
  "common.planning": "قيد التخطيط",
  "common.handover": "تسليم",
  "common.recipients": "المستلمون",
  "common.audience": "الجمهور",
  "common.internal": "داخلي",
  "common.client": "العميل",
  "common.dueDate": "تاريخ الاستحقاق",
  "common.uploadedBy": "تم الرفع بواسطة",
  "common.uploadedAt": "تاريخ الرفع",
  "common.lastUpdated": "آخر تحديث",
  "common.daysRemaining": "الأيام المتبقية",
  "common.overdue": "متأخر",
  "common.language": "اللغة",
  "common.preferredLanguage": "اللغة المفضلة",
  "report.projectName": "اسم المشروع",
  "report.reportDate": "تاريخ التقرير",
  "report.workCompleted": "الأعمال المنجزة",
  "report.photos": "الصور",
  "report.delays": "التأخيرات / المشكلات",
  "report.nextDayPlan": "خطة اليوم التالي",
  "report.pendingApprovals": "الموافقات المعلقة",
  "report.comments": "التعليقات",
  "report.viewFull": "عرض التقرير الكامل",
  "report.reportLanguage": "لغة التقرير",
  "report.sameAsRecipient": "نفس لغة المستلم المفضلة",
};

const zh = {
  "nav.dashboard": "仪表板",
  "nav.projects": "项目",
  "nav.team": "团队与权限",
  "nav.profile": "个人资料",
  "nav.logout": "退出登录",
  "nav.changePassword": "修改密码",
  "tab.overview": "概览",
  "tab.timeline": "时间表 / 甘特图",
  "tab.milestones": "里程碑",
  "tab.approvals": "审批",
  "tab.updates": "每日更新",
  "tab.photos": "照片",
  "tab.reports": "报告",
  "tab.financials": "财务",
  "tab.files": "文件",
  "status.planning": "规划中",
  "status.active": "进行中",
  "status.delayed": "已延误",
  "status.atRisk": "存在风险",
  "status.completed": "已完成",
  "status.handover": "移交",
  "status.awaitingApproval": "待审批",
  "status.reportsPending": "待发报告",
  "status.draft": "草稿",
  "status.sent": "已发送",
  "status.paid": "已付款",
  "status.overdue": "已逾期",
  "btn.uploadPhotos": "上传照片",
  "btn.addDailyUpdate": "添加每日更新",
  "btn.generateReport": "生成报告",
  "btn.sendReport": "发送报告",
  "btn.reply": "回复",
  "btn.viewReport": "查看报告",
  "btn.download": "下载",
  "btn.view": "查看",
  "btn.save": "保存",
  "btn.cancel": "取消",
  "btn.delete": "删除",
  "btn.discard": "丢弃",
  "btn.approveSend": "审批并发送",
  "btn.approveOnly": "仅审批",
  "btn.editDraft": "编辑草稿",
  "btn.upload": "上传",
  "btn.markFinal": "标记为最终版",
  "common.status": "状态",
  "common.progress": "进度",
  "common.milestone": "里程碑",
  "common.delayed": "延误",
  "common.completed": "完成",
  "common.active": "进行中",
  "common.planning": "规划中",
  "common.handover": "移交",
  "common.recipients": "收件人",
  "common.audience": "受众",
  "common.internal": "内部",
  "common.client": "客户",
  "common.dueDate": "截止日期",
  "common.uploadedBy": "上传者",
  "common.uploadedAt": "上传时间",
  "common.lastUpdated": "最近更新",
  "common.daysRemaining": "剩余天数",
  "common.overdue": "已逾期",
  "common.language": "语言",
  "common.preferredLanguage": "首选语言",
  "report.projectName": "项目名称",
  "report.reportDate": "报告日期",
  "report.workCompleted": "已完成工作",
  "report.photos": "照片",
  "report.delays": "延误 / 问题",
  "report.nextDayPlan": "次日计划",
  "report.pendingApprovals": "待审批事项",
  "report.comments": "评论",
  "report.viewFull": "查看完整报告",
  "report.reportLanguage": "报告语言",
  "report.sameAsRecipient": "与收件人首选语言相同",
};

// ---- Phase 2D.2 — comprehensive content translations ----
// Dashboard, project hero, tabs, modals, empty-states, hints.
// Keys grouped: dash.*, proj.*, hero.*, form.*, daily.*, photo.*,
// rep.*, file.*, fin.*, mile.*, empty.*, hint.*, msg.*.

const _moreEN = {
  // Dashboard titles & headings
  "dash.title.admin": "Admin Dashboard",
  "dash.title.manager": "My Projects",
  "dash.title.site": "Site Dashboard",
  "dash.title.my": "My Projects",
  "dash.section.liveOverview": "Live Project Overview",
  "dash.section.adminFinancials": "Admin-only financials overview",
  "dash.section.reportAutomation": "Report automation",
  "dash.section.recentActivity": "Recent activity",
  // Dashboard KPI cards
  "dash.kpi.active": "Active Projects",
  "dash.kpi.active.hint": "{n} in planning",
  "dash.kpi.delayed": "Delayed / At Risk",
  "dash.kpi.delayed.hint": "Needs attention",
  "dash.kpi.delayed.onTrack": "All on track",
  "dash.kpi.reportsPending": "Reports Pending",
  "dash.kpi.reportsPending.hint": "Drafts awaiting send",
  "dash.kpi.responses": "Pending Client Responses",
  "dash.kpi.responses.hint": "Awaiting reply",
  "dash.kpi.milestones": "Upcoming Milestones",
  "dash.kpi.milestones.hint": "Next 30 days",
  "dash.kpi.uploads": "Recent Uploads",
  "dash.kpi.uploads.hint": "Last 7 days",
  // Financial KPI
  "fin.kpi.totalValue": "Total project value",
  "fin.kpi.invoiced": "Amount invoiced",
  "fin.kpi.received": "Amount received",
  "fin.kpi.paymentMilestones": "Payment milestones",
  "fin.kpi.balance": "Balance {amount}",
  "fin.kpi.portfolioPct": "{pct}% of portfolio",
  "fin.kpi.dueOverdue": "{due} due · {overdue} overdue",
  "fin.adminBadge": "Admin",
  // Automation KPI
  "auto.kpi.dailyScheduled": "Daily scheduled",
  "auto.kpi.weeklyScheduled": "Weekly scheduled",
  "auto.kpi.awaitingApproval": "Awaiting approval",
  "auto.kpi.failedToday": "Failed today",
  "auto.kpi.sentThisWeek": "Sent this week",
  // Project card
  "proj.card.progress": "Progress",
  "proj.card.target": "Target {date}",
  "proj.card.daysLeft": "{n} d left",
  "proj.card.daysOverdue": "{n} d overdue",
  // Buttons / actions
  "btn.newProject": "New Project",
  "btn.usersAccess": "Users & Access",
  "btn.close": "Close",
  "btn.create": "Create",
  "btn.add": "Add",
  "btn.edit": "Edit",
  "btn.remove": "Remove",
  "btn.send": "Send",
  "btn.submit": "Submit",
  "btn.next": "Next",
  "btn.back": "Back",
  "btn.confirm": "Confirm",
  "btn.testSendToMe": "Test send to me",
  // Loading / empty states
  "msg.loading": "Loading…",
  "msg.loadingProjects": "Loading projects…",
  "msg.noProjects": "No projects yet.",
  "msg.noProjectsAdmin": 'Click "New Project" above to create your first project. You can assign clients and team members once it\'s created.',
  "msg.noProjectsClient": "You haven't been added to any projects yet. Your project manager will invite you soon.",
  "msg.saving": "Saving…",
  "msg.sending": "Sending…",
  "msg.uploading": "Uploading…",
  "msg.success": "Success",
  "msg.error": "Something went wrong",
  // Project count
  "proj.count.one": "{n} project",
  "proj.count.other": "{n} projects",
  // Project hero
  "hero.target": "Target",
  "hero.start": "Start",
  "hero.client": "Client",
  "hero.location": "Location",
  "hero.consultant": "Consultant",
  "hero.pm": "Project Manager",
  "hero.siteEngineer": "Site Engineer",
  "hero.type": "Type",
  "hero.code": "Code",
  "hero.totalProgress": "Total progress",
  "hero.activePhase": "Active Phase",
  "hero.programmeNotLoaded": "Programme not loaded",
  "hero.complete": "complete",
  "hero.uploadGantt": "Upload Gantt to begin",
  "hero.noMilestoneSet": "No milestone set",
  "hero.targetCompletion": "Target Completion",
  "hero.notSet": "Not set",
  "hero.unassigned": "Unassigned",
  "hero.assignInTeam": "Assign in team & access",
  "hero.teamMembers": "Team Members",
  "hero.onProject": "{n} on project",
  "hero.includingClientSubs": "Including client & subs",
  "hero.latestUpdate": "Latest Site Update",
  "hero.overallProgress": "Overall Progress",
  "hero.projectPhases": "Project Phases",
  "hero.projectWorkstreams": "Project Workstreams",
  "hero.primarySitePhase": "Primary Site Phase",
  "hero.nextOnSite": "Next on site",
  "hero.statusByWorkstream": "Project Status by Workstream",
  "hero.workstreamsNote": "Approvals, procurement and site works run in parallel — procurement does not hold up site activities.",
  "hero.stage.one": "{n} stage",
  "hero.stage.other": "{n} stages",
  "hero.daysRemaining": "{n} days remaining",
  "hero.pendingClientActions": "{n} pending client action(s)",
  "hero.riskAlerts": "Risk Alerts",
  "hero.keyDocuments": "Key Documents",
  "hero.openFiles": "Open files",
  "hero.quickAccess": "Quick access",
  // Daily updates
  "daily.title": "Daily Updates",
  "daily.empty": "No daily updates yet.",
  "daily.workCompleted": "Work completed",
  "daily.delays": "Delays / Issues",
  "daily.nextPlan": "Next day plan",
  "daily.manpower": "Manpower",
  "daily.materials": "Materials delivered",
  "daily.weather": "Weather",
  "daily.visibility": "Visibility",
  "daily.linkOptional": "Link to programme activity (optional)",
  "daily.phaseLabel": "Phase",
  "daily.taskLabel": "Gantt task",
  "daily.milestoneLabel": "Milestone",
  "daily.subcontractor": "Subcontractor scope",
  "daily.taskStatus": "Update task status",
  "daily.taskProgress": "Task progress %",
  "daily.milestoneStatus": "Update milestone status",
  "daily.milestoneProgress": "Milestone progress %",
  "daily.keepAsIs": "— Keep as is —",
  "daily.none": "— None —",
  "daily.translateThis": "Translate this update",
  // Photos
  "photo.title": "Photos",
  "photo.empty": "No photos uploaded yet.",
  "photo.caption": "Caption",
  "photo.addCaption": "Add a caption…",
  "photo.dropHint": "Drag photos here or click to select",
  "photo.maxSize": "Max 25 MB per photo · auto-compressed",
  // Reports
  "rep.title": "Reports",
  "rep.empty": "No reports generated yet.",
  "rep.generate": "Generate Report",
  "rep.generateDialog.title": "Generate {kind} report",
  "rep.kind.daily": "Daily",
  "rep.kind.weekly": "Weekly",
  "rep.audience": "Audience",
  "rep.audience.internal": "Internal (Time Furniture team)",
  "rep.audience.client": "Client",
  "rep.includeFinancials": "Include financials (admin only)",
  "rep.reportLanguage": "Report language",
  "rep.langAuto": "Auto · Same as recipient's preferred language (recommended for client emails)",
  "rep.langHint": "Headings, status labels and section titles are translated. User-typed content (work-completed notes, comments, file names) is kept verbatim.",
  "rep.recipients": "Recipients",
  "rep.recipientsHint": "Comma-separated email addresses",
  "rep.draftStatus": "Draft",
  "rep.sentStatus": "Sent",
  "rep.discardConfirm": "Discard this draft report?",
  // Files
  "file.title": "Files",
  "file.empty": "No files uploaded yet.",
  "file.uploadHint": "Drag files here or click to select",
  "file.maxSize": "Max 100 MB per file",
  "file.markFinal": "Mark as Final",
  "file.unmarkFinal": "Unmark Final",
  "file.keyDocs": "Final files & key documents",
  "file.keyDocs.empty": "No documents have been marked as final yet.",
  "file.deleteConfirm": "Delete this file?",
  // Milestones
  "mile.title": "Milestones",
  "mile.empty": "No milestones defined.",
  "mile.add": "Add Milestone",
  "mile.next": "Next milestone",
  "mile.setNext": "Set as next",
  "mile.target": "Target {date}",
  "mile.statusPlanned": "Planned",
  "mile.statusInProgress": "In progress",
  "mile.statusCompleted": "Completed",
  "mile.statusDelayed": "Delayed",
  "mile.statusBlocked": "Blocked",
  "mile.showUpdates": "Show site updates",
  "mile.hideUpdates": "Hide site updates",
  "mile.noUpdatesLinked": "No site updates linked to this milestone yet.",
  "mile.openDetails": "Open",
  "mile.viewAll": "View all milestones",
  "mile.backToSummary": "Back to summary",
  "mile.search": "Search milestones…",
  "mile.responsible": "Responsible",
  "mile.dependency": "Dependency",
  "mile.linkedUpdates": "Linked site updates",
  "mile.sec.currentNext": "Current & Next milestone",
  "mile.sec.currentEmpty": "No milestone is flagged as current. Set one to highlight this week's focus.",
  "mile.sec.thisWeek": "Due this week",
  "mile.sec.thisWeekEmpty": "No milestones due this week.",
  "mile.sec.delayed": "Delayed or high-risk",
  "mile.sec.delayedEmpty": "No delayed or blocked milestones — everything is on track.",
  "mile.filter.upcoming": "Upcoming",
  "mile.filter.thisWeek": "This week",
  "mile.filter.nextWeek": "Next week",
  "nav.all": "All",
  // Financials tab
  "fin.title": "Financials (Admin)",
  "fin.lockedClient": "Financials are admin-only and shared on a per-project basis.",
  "fin.import": "Import financials Excel",
  "fin.totalValue": "Total project value",
  "fin.byStage": "Stage breakdown",
  "fin.paymentMilestones": "Payment milestones",
  // Forms
  "form.required": "Required",
  "form.optional": "Optional",
  "form.email": "Email",
  "form.name": "Name",
  "form.role": "Role",
  "form.company": "Company",
  "form.phone": "Phone",
  "form.notes": "Notes",
  "form.description": "Description",
  "form.startDate": "Start date",
  "form.targetDate": "Target completion",
  "form.password": "Password",
  "form.newPassword": "New password",
  "form.confirmPassword": "Confirm password",
  "form.preferredLanguage": "Preferred language",
  "form.preferredLanguageHint": "Used for portal labels and invitation/report emails for this user.",
  "form.projectName": "Project name",
  "form.projectCode": "Project code",
  "form.projectLocation": "Location",
  "form.projectClient": "Client",
  "form.defaultLanguage": "Project default language",
  // Comments
  "comment.title": "Comments",
  "comment.empty": "No comments yet.",
  "comment.placeholder": "Write a comment…",
  "comment.post": "Post Comment",
  "comment.replyPlaceholder": "Write a reply…",
  // Misc
  "msg.confirmDelete": "Are you sure you want to delete this?",
  "msg.changesSaved": "Changes saved",
  "msg.languageChanged": "Language changed to {lang}",
};

const _moreKO = {
  "dash.title.admin": "관리자 대시보드",
  "dash.title.manager": "내 프로젝트",
  "dash.title.site": "현장 대시보드",
  "dash.title.my": "내 프로젝트",
  "dash.section.liveOverview": "실시간 프로젝트 개요",
  "dash.section.adminFinancials": "관리자 전용 재무 개요",
  "dash.section.reportAutomation": "보고서 자동화",
  "dash.section.recentActivity": "최근 활동",
  "dash.kpi.active": "진행 중인 프로젝트",
  "dash.kpi.active.hint": "계획 단계 {n}건",
  "dash.kpi.delayed": "지연 / 위험",
  "dash.kpi.delayed.hint": "확인 필요",
  "dash.kpi.delayed.onTrack": "모두 정상 진행",
  "dash.kpi.reportsPending": "발송 대기 보고서",
  "dash.kpi.reportsPending.hint": "발송 대기 중인 초안",
  "dash.kpi.responses": "고객 응답 대기",
  "dash.kpi.responses.hint": "회신 대기 중",
  "dash.kpi.milestones": "예정 마일스톤",
  "dash.kpi.milestones.hint": "향후 30일",
  "dash.kpi.uploads": "최근 업로드",
  "dash.kpi.uploads.hint": "최근 7일",
  "fin.kpi.totalValue": "총 프로젝트 가치",
  "fin.kpi.invoiced": "청구 금액",
  "fin.kpi.received": "수금 금액",
  "fin.kpi.paymentMilestones": "결제 마일스톤",
  "fin.kpi.balance": "잔액 {amount}",
  "fin.kpi.portfolioPct": "포트폴리오의 {pct}%",
  "fin.kpi.dueOverdue": "{due}건 예정 · {overdue}건 연체",
  "fin.adminBadge": "관리자",
  "auto.kpi.dailyScheduled": "일일 예약",
  "auto.kpi.weeklyScheduled": "주간 예약",
  "auto.kpi.awaitingApproval": "승인 대기",
  "auto.kpi.failedToday": "오늘 실패",
  "auto.kpi.sentThisWeek": "이번 주 발송",
  "proj.card.progress": "진행률",
  "proj.card.target": "목표 {date}",
  "proj.card.daysLeft": "{n}일 남음",
  "proj.card.daysOverdue": "{n}일 지연",
  "btn.newProject": "새 프로젝트",
  "btn.usersAccess": "사용자 및 접근 권한",
  "btn.close": "닫기",
  "btn.create": "생성",
  "btn.add": "추가",
  "btn.edit": "편집",
  "btn.remove": "제거",
  "btn.send": "보내기",
  "btn.submit": "제출",
  "btn.next": "다음",
  "btn.back": "뒤로",
  "btn.confirm": "확인",
  "btn.testSendToMe": "내게 테스트 발송",
  "msg.loading": "로딩 중…",
  "msg.loadingProjects": "프로젝트를 불러오는 중…",
  "msg.noProjects": "아직 프로젝트가 없습니다.",
  "msg.noProjectsAdmin": "위의 \"새 프로젝트\" 버튼을 눌러 첫 프로젝트를 생성하세요. 생성 후 고객과 팀원을 배정할 수 있습니다.",
  "msg.noProjectsClient": "아직 어떤 프로젝트에도 추가되지 않았습니다. 프로젝트 매니저가 곧 초대할 예정입니다.",
  "msg.saving": "저장 중…",
  "msg.sending": "전송 중…",
  "msg.uploading": "업로드 중…",
  "msg.success": "성공",
  "msg.error": "오류가 발생했습니다",
  "proj.count.one": "프로젝트 {n}건",
  "proj.count.other": "프로젝트 {n}건",
  "hero.target": "목표일",
  "hero.start": "시작",
  "hero.client": "고객사",
  "hero.location": "위치",
  "hero.consultant": "컨설턴트",
  "hero.pm": "프로젝트 매니저",
  "hero.siteEngineer": "현장 엔지니어",
  "hero.type": "유형",
  "hero.code": "코드",
  "hero.totalProgress": "전체 진행률",
  "hero.activePhase": "현재 공정",
  "hero.programmeNotLoaded": "프로그램이 로드되지 않음",
  "hero.complete": "완료",
  "hero.uploadGantt": "간트 차트를 업로드하세요",
  "hero.noMilestoneSet": "설정된 마일스톤 없음",
  "hero.targetCompletion": "목표 완료일",
  "hero.notSet": "설정되지 않음",
  "hero.unassigned": "미배정",
  "hero.assignInTeam": "팀 및 접근 권한에서 배정",
  "hero.teamMembers": "팀원",
  "hero.onProject": "{n}명 참여",
  "hero.includingClientSubs": "고객 및 협력업체 포함",
  "hero.latestUpdate": "최신 현장 업데이트",
  "hero.overallProgress": "전체 진행률",
  "hero.projectPhases": "프로젝트 공정",
  "hero.projectWorkstreams": "프로젝트 워크스트림",
  "hero.primarySitePhase": "주요 현장 공정",
  "hero.nextOnSite": "다음 현장 작업",
  "hero.statusByWorkstream": "워크스트림별 프로젝트 현황",
  "hero.workstreamsNote": "승인, 조달, 현장 작업은 병행 진행됩니다 — 조달이 현장 작업을 지연시키지 않습니다.",
  "hero.stage.one": "{n}단계",
  "hero.stage.other": "{n}단계",
  "hero.daysRemaining": "{n}일 남음",
  "hero.pendingClientActions": "고객 응답 대기 {n}건",
  "hero.riskAlerts": "리스크 경고",
  "hero.keyDocuments": "주요 문서",
  "hero.openFiles": "파일 열기",
  "hero.quickAccess": "빠른 접근",
  "daily.title": "일일 업데이트",
  "daily.empty": "아직 일일 업데이트가 없습니다.",
  "daily.workCompleted": "완료된 작업",
  "daily.delays": "지연 / 이슈",
  "daily.nextPlan": "다음 날 계획",
  "daily.manpower": "투입 인력",
  "daily.materials": "납품 자재",
  "daily.weather": "날씨",
  "daily.visibility": "공개 범위",
  "daily.linkOptional": "프로그램 활동에 연결 (선택)",
  "daily.phaseLabel": "공정",
  "daily.taskLabel": "간트 작업",
  "daily.milestoneLabel": "마일스톤",
  "daily.subcontractor": "협력업체 범위",
  "daily.taskStatus": "작업 상태 업데이트",
  "daily.taskProgress": "작업 진행률 %",
  "daily.milestoneStatus": "마일스톤 상태 업데이트",
  "daily.milestoneProgress": "마일스톤 진행률 %",
  "daily.keepAsIs": "— 유지 —",
  "daily.none": "— 없음 —",
  "daily.translateThis": "이 업데이트 번역",
  "photo.title": "사진",
  "photo.empty": "아직 업로드된 사진이 없습니다.",
  "photo.caption": "캡션",
  "photo.addCaption": "캡션 입력…",
  "photo.dropHint": "사진을 끌어다 놓거나 클릭하여 선택하세요",
  "photo.maxSize": "사진 1장당 최대 25MB · 자동 압축",
  "rep.title": "보고서",
  "rep.empty": "아직 생성된 보고서가 없습니다.",
  "rep.generate": "보고서 생성",
  "rep.generateDialog.title": "{kind} 보고서 생성",
  "rep.kind.daily": "일일",
  "rep.kind.weekly": "주간",
  "rep.audience": "대상",
  "rep.audience.internal": "내부 (Time Furniture 팀)",
  "rep.audience.client": "고객",
  "rep.includeFinancials": "재무 포함 (관리자 전용)",
  "rep.reportLanguage": "보고서 언어",
  "rep.langAuto": "자동 · 수신자의 선호 언어 사용 (고객 이메일에 권장)",
  "rep.langHint": "제목, 상태 라벨, 섹션 제목이 번역됩니다. 사용자가 입력한 내용(작업 노트, 댓글, 파일명)은 원문 그대로 유지됩니다.",
  "rep.recipients": "수신자",
  "rep.recipientsHint": "쉼표로 구분된 이메일 주소",
  "rep.draftStatus": "초안",
  "rep.sentStatus": "발송됨",
  "rep.discardConfirm": "이 초안 보고서를 폐기하시겠습니까?",
  "file.title": "파일",
  "file.empty": "아직 업로드된 파일이 없습니다.",
  "file.uploadHint": "파일을 끌어다 놓거나 클릭하여 선택하세요",
  "file.maxSize": "파일 1개당 최대 100MB",
  "file.markFinal": "최종본으로 지정",
  "file.unmarkFinal": "최종본 지정 해제",
  "file.keyDocs": "최종 파일 및 주요 문서",
  "file.keyDocs.empty": "아직 최종본으로 지정된 문서가 없습니다.",
  "file.deleteConfirm": "이 파일을 삭제하시겠습니까?",
  "mile.title": "마일스톤",
  "mile.empty": "정의된 마일스톤이 없습니다.",
  "mile.add": "마일스톤 추가",
  "mile.next": "다음 마일스톤",
  "mile.setNext": "다음으로 설정",
  "mile.target": "목표 {date}",
  "mile.statusPlanned": "예정",
  "mile.statusInProgress": "진행 중",
  "mile.statusCompleted": "완료",
  "mile.statusDelayed": "지연됨",
  "mile.statusBlocked": "차단됨",
  "mile.showUpdates": "현장 업데이트 보기",
  "mile.hideUpdates": "현장 업데이트 숨기기",
  "mile.noUpdatesLinked": "이 마일스톤에 연결된 현장 업데이트가 아직 없습니다.",
  "mile.openDetails": "열기",
  "mile.viewAll": "전체 마일스톤 보기",
  "mile.backToSummary": "요약으로 돌아가기",
  "mile.search": "마일스톤 검색…",
  "mile.responsible": "담당",
  "mile.dependency": "의존",
  "mile.linkedUpdates": "연결된 현장 업데이트",
  "mile.sec.currentNext": "현재 및 다음 마일스톤",
  "mile.sec.currentEmpty": "현재로 표시된 마일스톤이 없습니다. 이번 주에 집중할 항목을 지정하세요.",
  "mile.sec.thisWeek": "이번 주 만기",
  "mile.sec.thisWeekEmpty": "이번 주 만기인 마일스톤이 없습니다.",
  "mile.sec.delayed": "지연 또는 위험",
  "mile.sec.delayedEmpty": "지연되거나 차단된 마일스톤이 없습니다 — 모두 정상 진행 중입니다.",
  "mile.filter.upcoming": "예정",
  "mile.filter.thisWeek": "이번 주",
  "mile.filter.nextWeek": "다음 주",
  "nav.all": "전체",
  "fin.title": "재무 (관리자)",
  "fin.lockedClient": "재무 정보는 관리자 전용이며 프로젝트별로 공유됩니다.",
  "fin.import": "재무 엑셀 가져오기",
  "fin.totalValue": "총 프로젝트 가치",
  "fin.byStage": "단계별 분석",
  "fin.paymentMilestones": "결제 마일스톤",
  "form.required": "필수",
  "form.optional": "선택",
  "form.email": "이메일",
  "form.name": "이름",
  "form.role": "역할",
  "form.company": "회사",
  "form.phone": "전화번호",
  "form.notes": "메모",
  "form.description": "설명",
  "form.startDate": "시작일",
  "form.targetDate": "목표 완료일",
  "form.password": "비밀번호",
  "form.newPassword": "새 비밀번호",
  "form.confirmPassword": "비밀번호 확인",
  "form.preferredLanguage": "선호 언어",
  "form.preferredLanguageHint": "이 사용자의 포털 라벨 및 초대/보고서 이메일에 사용됩니다.",
  "form.projectName": "프로젝트명",
  "form.projectCode": "프로젝트 코드",
  "form.projectLocation": "위치",
  "form.projectClient": "고객사",
  "form.defaultLanguage": "프로젝트 기본 언어",
  "comment.title": "댓글",
  "comment.empty": "아직 댓글이 없습니다.",
  "comment.placeholder": "댓글을 작성하세요…",
  "comment.post": "댓글 게시",
  "comment.replyPlaceholder": "답글을 작성하세요…",
  "msg.confirmDelete": "정말 삭제하시겠습니까?",
  "msg.changesSaved": "변경 사항이 저장되었습니다",
  "msg.languageChanged": "언어가 {lang}(으)로 변경되었습니다",
};

const _moreAR = {
  "dash.title.admin": "لوحة تحكم المشرف",
  "dash.title.manager": "مشاريعي",
  "dash.title.site": "لوحة الموقع",
  "dash.title.my": "مشاريعي",
  "dash.section.liveOverview": "نظرة عامة مباشرة على المشاريع",
  "dash.section.adminFinancials": "نظرة مالية للمشرف فقط",
  "dash.section.reportAutomation": "أتمتة التقارير",
  "dash.section.recentActivity": "النشاط الأخير",
  "dash.kpi.active": "المشاريع النشطة",
  "dash.kpi.active.hint": "{n} قيد التخطيط",
  "dash.kpi.delayed": "متأخر / في خطر",
  "dash.kpi.delayed.hint": "يحتاج إلى انتباه",
  "dash.kpi.delayed.onTrack": "كل شيء على المسار",
  "dash.kpi.reportsPending": "تقارير معلقة",
  "dash.kpi.reportsPending.hint": "مسودات بانتظار الإرسال",
  "dash.kpi.responses": "ردود العميل المعلقة",
  "dash.kpi.responses.hint": "بانتظار الرد",
  "dash.kpi.milestones": "معالم قادمة",
  "dash.kpi.milestones.hint": "خلال الـ30 يومًا القادمة",
  "dash.kpi.uploads": "الرفعات الأخيرة",
  "dash.kpi.uploads.hint": "آخر 7 أيام",
  "fin.kpi.totalValue": "إجمالي قيمة المشروع",
  "fin.kpi.invoiced": "المبلغ المفوتر",
  "fin.kpi.received": "المبلغ المحصّل",
  "fin.kpi.paymentMilestones": "معالم الدفع",
  "fin.kpi.balance": "الرصيد {amount}",
  "fin.kpi.portfolioPct": "{pct}% من المحفظة",
  "fin.kpi.dueOverdue": "{due} مستحق · {overdue} متأخر",
  "fin.adminBadge": "مشرف",
  "auto.kpi.dailyScheduled": "مجدول يوميًا",
  "auto.kpi.weeklyScheduled": "مجدول أسبوعيًا",
  "auto.kpi.awaitingApproval": "بانتظار الموافقة",
  "auto.kpi.failedToday": "فشل اليوم",
  "auto.kpi.sentThisWeek": "أُرسل هذا الأسبوع",
  "proj.card.progress": "التقدم",
  "proj.card.target": "الهدف {date}",
  "proj.card.daysLeft": "{n} يوم متبقي",
  "proj.card.daysOverdue": "{n} يوم تأخير",
  "btn.newProject": "مشروع جديد",
  "btn.usersAccess": "المستخدمون والصلاحيات",
  "btn.close": "إغلاق",
  "btn.create": "إنشاء",
  "btn.add": "إضافة",
  "btn.edit": "تعديل",
  "btn.remove": "إزالة",
  "btn.send": "إرسال",
  "btn.submit": "إرسال",
  "btn.next": "التالي",
  "btn.back": "رجوع",
  "btn.confirm": "تأكيد",
  "btn.testSendToMe": "إرسال تجريبي إليّ",
  "msg.loading": "جارٍ التحميل…",
  "msg.loadingProjects": "جارٍ تحميل المشاريع…",
  "msg.noProjects": "لا توجد مشاريع بعد.",
  "msg.noProjectsAdmin": "اضغط على \"مشروع جديد\" أعلاه لإنشاء أول مشروع. يمكنك تعيين العملاء وأعضاء الفريق بعد الإنشاء.",
  "msg.noProjectsClient": "لم تتم إضافتك إلى أي مشروع بعد. سيقوم مدير المشروع بدعوتك قريبًا.",
  "msg.saving": "جارٍ الحفظ…",
  "msg.sending": "جارٍ الإرسال…",
  "msg.uploading": "جارٍ الرفع…",
  "msg.success": "تم بنجاح",
  "msg.error": "حدث خطأ ما",
  "proj.count.one": "{n} مشروع",
  "proj.count.other": "{n} مشاريع",
  "hero.target": "الهدف",
  "hero.start": "البداية",
  "hero.client": "العميل",
  "hero.location": "الموقع",
  "hero.consultant": "الاستشاري",
  "hero.pm": "مدير المشروع",
  "hero.siteEngineer": "مهندس الموقع",
  "hero.type": "النوع",
  "hero.code": "الرمز",
  "hero.totalProgress": "إجمالي التقدم",
  "hero.activePhase": "المرحلة الحالية",
  "hero.programmeNotLoaded": "لم يتم تحميل البرنامج",
  "hero.complete": "مكتمل",
  "hero.uploadGantt": "ارفع ملف غانت للبدء",
  "hero.noMilestoneSet": "لا يوجد معلم محدد",
  "hero.targetCompletion": "تاريخ الإنجاز المستهدف",
  "hero.notSet": "غير محدد",
  "hero.unassigned": "غير مُسند",
  "hero.assignInTeam": "أسند من خلال الفريق والصلاحيات",
  "hero.teamMembers": "أعضاء الفريق",
  "hero.onProject": "{n} في المشروع",
  "hero.includingClientSubs": "شاملاً العميل والمقاولين",
  "hero.latestUpdate": "آخر تحديث للموقع",
  "hero.overallProgress": "إجمالي التقدم",
  "hero.projectPhases": "مراحل المشروع",
  "hero.projectWorkstreams": "مسارات عمل المشروع",
  "hero.primarySitePhase": "المرحلة الميدانية الرئيسية",
  "hero.nextOnSite": "النشاط الميداني التالي",
  "hero.statusByWorkstream": "حالة المشروع حسب مسار العمل",
  "hero.workstreamsNote": "تسير الموافقات والمشتريات وأعمال الموقع بالتوازي — المشتريات لا تعطل أنشطة الموقع.",
  "hero.stage.one": "{n} مرحلة",
  "hero.stage.other": "{n} مراحل",
  "hero.daysRemaining": "{n} يوم متبقي",
  "hero.pendingClientActions": "{n} إجراء معلق للعميل",
  "hero.riskAlerts": "تنبيهات المخاطر",
  "hero.keyDocuments": "المستندات الرئيسية",
  "hero.openFiles": "فتح الملفات",
  "hero.quickAccess": "وصول سريع",
  "daily.title": "التحديثات اليومية",
  "daily.empty": "لا توجد تحديثات يومية بعد.",
  "daily.workCompleted": "الأعمال المنجزة",
  "daily.delays": "التأخيرات / المشكلات",
  "daily.nextPlan": "خطة اليوم التالي",
  "daily.manpower": "الأيدي العاملة",
  "daily.materials": "المواد الموردة",
  "daily.weather": "الطقس",
  "daily.visibility": "نطاق الرؤية",
  "daily.linkOptional": "ربط بنشاط البرنامج (اختياري)",
  "daily.phaseLabel": "المرحلة",
  "daily.taskLabel": "مهمة غانت",
  "daily.milestoneLabel": "معلم",
  "daily.subcontractor": "نطاق المقاول من الباطن",
  "daily.taskStatus": "تحديث حالة المهمة",
  "daily.taskProgress": "تقدم المهمة %",
  "daily.milestoneStatus": "تحديث حالة المعلم",
  "daily.milestoneProgress": "تقدم المعلم %",
  "daily.keepAsIs": "— الإبقاء كما هو —",
  "daily.none": "— لا شيء —",
  "daily.translateThis": "ترجم هذا التحديث",
  "photo.title": "الصور",
  "photo.empty": "لم يتم رفع صور بعد.",
  "photo.caption": "تعليق",
  "photo.addCaption": "أضف تعليقًا…",
  "photo.dropHint": "اسحب الصور إلى هنا أو انقر للاختيار",
  "photo.maxSize": "حد أقصى 25 ميجابايت للصورة · ضغط تلقائي",
  "rep.title": "التقارير",
  "rep.empty": "لم يتم إنشاء تقارير بعد.",
  "rep.generate": "إنشاء تقرير",
  "rep.generateDialog.title": "إنشاء تقرير {kind}",
  "rep.kind.daily": "يومي",
  "rep.kind.weekly": "أسبوعي",
  "rep.audience": "الجمهور",
  "rep.audience.internal": "داخلي (فريق Time Furniture)",
  "rep.audience.client": "العميل",
  "rep.includeFinancials": "تضمين الشؤون المالية (للمشرف فقط)",
  "rep.reportLanguage": "لغة التقرير",
  "rep.langAuto": "تلقائي · بنفس لغة المستلم المفضلة (يوصى به للبريد الموجه للعملاء)",
  "rep.langHint": "تُترجم العناوين وتسميات الحالة وعناوين الأقسام. أما المحتوى الذي يكتبه المستخدمون (ملاحظات العمل، التعليقات، أسماء الملفات) فيبقى كما هو.",
  "rep.recipients": "المستلمون",
  "rep.recipientsHint": "عناوين بريد إلكتروني مفصولة بفواصل",
  "rep.draftStatus": "مسودة",
  "rep.sentStatus": "تم الإرسال",
  "rep.discardConfirm": "هل تريد التخلص من مسودة هذا التقرير؟",
  "file.title": "الملفات",
  "file.empty": "لم يتم رفع ملفات بعد.",
  "file.uploadHint": "اسحب الملفات إلى هنا أو انقر للاختيار",
  "file.maxSize": "حد أقصى 100 ميجابايت لكل ملف",
  "file.markFinal": "تعيين كنسخة نهائية",
  "file.unmarkFinal": "إلغاء التعيين كنسخة نهائية",
  "file.keyDocs": "الملفات النهائية والمستندات الرئيسية",
  "file.keyDocs.empty": "لم يتم تعيين أي مستندات كنسخة نهائية بعد.",
  "file.deleteConfirm": "هل تريد حذف هذا الملف؟",
  "mile.title": "المعالم",
  "mile.empty": "لم يتم تحديد معالم.",
  "mile.add": "إضافة معلم",
  "mile.next": "المعلم التالي",
  "mile.setNext": "تعيين كمعلم تالٍ",
  "mile.target": "الهدف {date}",
  "mile.statusPlanned": "مخطط",
  "mile.statusInProgress": "قيد التنفيذ",
  "mile.statusCompleted": "مكتمل",
  "mile.statusDelayed": "متأخر",
  "mile.statusBlocked": "متوقف",
  "mile.showUpdates": "عرض تحديثات الموقع",
  "mile.hideUpdates": "إخفاء تحديثات الموقع",
  "mile.noUpdatesLinked": "لا توجد تحديثات موقع مرتبطة بهذا المعلم بعد.",
  "mile.openDetails": "فتح",
  "mile.viewAll": "عرض جميع المعالم",
  "mile.backToSummary": "العودة إلى الملخص",
  "mile.search": "ابحث في المعالم…",
  "mile.responsible": "المسؤول",
  "mile.dependency": "التبعية",
  "mile.linkedUpdates": "تحديثات الموقع المرتبطة",
  "mile.sec.currentNext": "المعلم الحالي والتالي",
  "mile.sec.currentEmpty": "لا يوجد معلم محدد كحالي. حدّد أحدها لإبراز تركيز هذا الأسبوع.",
  "mile.sec.thisWeek": "مستحقة هذا الأسبوع",
  "mile.sec.thisWeekEmpty": "لا توجد معالم مستحقة هذا الأسبوع.",
  "mile.sec.delayed": "متأخر أو عالي المخاطر",
  "mile.sec.delayedEmpty": "لا توجد معالم متأخرة أو متوقفة — كل شيء على المسار.",
  "mile.filter.upcoming": "قادم",
  "mile.filter.thisWeek": "هذا الأسبوع",
  "mile.filter.nextWeek": "الأسبوع القادم",
  "nav.all": "الكل",
  "fin.title": "الشؤون المالية (للمشرف)",
  "fin.lockedClient": "الشؤون المالية متاحة للمشرف فقط وتتم مشاركتها لكل مشروع على حدة.",
  "fin.import": "استيراد ملف Excel للشؤون المالية",
  "fin.totalValue": "إجمالي قيمة المشروع",
  "fin.byStage": "تفصيل المراحل",
  "fin.paymentMilestones": "معالم الدفع",
  "form.required": "مطلوب",
  "form.optional": "اختياري",
  "form.email": "البريد الإلكتروني",
  "form.name": "الاسم",
  "form.role": "الدور",
  "form.company": "الشركة",
  "form.phone": "الهاتف",
  "form.notes": "ملاحظات",
  "form.description": "الوصف",
  "form.startDate": "تاريخ البدء",
  "form.targetDate": "تاريخ الإنجاز المستهدف",
  "form.password": "كلمة المرور",
  "form.newPassword": "كلمة المرور الجديدة",
  "form.confirmPassword": "تأكيد كلمة المرور",
  "form.preferredLanguage": "اللغة المفضلة",
  "form.preferredLanguageHint": "تُستخدم لتسميات البوابة وبريد الدعوة/التقارير لهذا المستخدم.",
  "form.projectName": "اسم المشروع",
  "form.projectCode": "رمز المشروع",
  "form.projectLocation": "الموقع",
  "form.projectClient": "العميل",
  "form.defaultLanguage": "اللغة الافتراضية للمشروع",
  "comment.title": "التعليقات",
  "comment.empty": "لا توجد تعليقات بعد.",
  "comment.placeholder": "اكتب تعليقًا…",
  "comment.post": "نشر التعليق",
  "comment.replyPlaceholder": "اكتب ردًا…",
  "msg.confirmDelete": "هل أنت متأكد من الحذف؟",
  "msg.changesSaved": "تم حفظ التغييرات",
  "msg.languageChanged": "تم تغيير اللغة إلى {lang}",
};

const _moreZH = {
  "dash.title.admin": "管理员仪表板",
  "dash.title.manager": "我的项目",
  "dash.title.site": "现场仪表板",
  "dash.title.my": "我的项目",
  "dash.section.liveOverview": "项目实时概览",
  "dash.section.adminFinancials": "管理员专用财务概览",
  "dash.section.reportAutomation": "报告自动化",
  "dash.section.recentActivity": "近期动态",
  "dash.kpi.active": "进行中的项目",
  "dash.kpi.active.hint": "规划中 {n} 个",
  "dash.kpi.delayed": "延误 / 风险",
  "dash.kpi.delayed.hint": "需要关注",
  "dash.kpi.delayed.onTrack": "一切正常",
  "dash.kpi.reportsPending": "待发报告",
  "dash.kpi.reportsPending.hint": "待发送的草稿",
  "dash.kpi.responses": "待客户回复",
  "dash.kpi.responses.hint": "等待回复中",
  "dash.kpi.milestones": "即将到来的里程碑",
  "dash.kpi.milestones.hint": "未来 30 天",
  "dash.kpi.uploads": "最近上传",
  "dash.kpi.uploads.hint": "最近 7 天",
  "fin.kpi.totalValue": "项目总价值",
  "fin.kpi.invoiced": "已开票金额",
  "fin.kpi.received": "已收款金额",
  "fin.kpi.paymentMilestones": "付款里程碑",
  "fin.kpi.balance": "余额 {amount}",
  "fin.kpi.portfolioPct": "占组合 {pct}%",
  "fin.kpi.dueOverdue": "{due} 项到期 · {overdue} 项逾期",
  "fin.adminBadge": "管理员",
  "auto.kpi.dailyScheduled": "每日计划",
  "auto.kpi.weeklyScheduled": "每周计划",
  "auto.kpi.awaitingApproval": "等待审批",
  "auto.kpi.failedToday": "今日失败",
  "auto.kpi.sentThisWeek": "本周已发送",
  "proj.card.progress": "进度",
  "proj.card.target": "目标 {date}",
  "proj.card.daysLeft": "剩余 {n} 天",
  "proj.card.daysOverdue": "逾期 {n} 天",
  "btn.newProject": "新建项目",
  "btn.usersAccess": "用户与权限",
  "btn.close": "关闭",
  "btn.create": "创建",
  "btn.add": "添加",
  "btn.edit": "编辑",
  "btn.remove": "移除",
  "btn.send": "发送",
  "btn.submit": "提交",
  "btn.next": "下一步",
  "btn.back": "返回",
  "btn.confirm": "确认",
  "btn.testSendToMe": "测试发送给我",
  "msg.loading": "加载中…",
  "msg.loadingProjects": "正在加载项目…",
  "msg.noProjects": "暂无项目。",
  "msg.noProjectsAdmin": "点击上方的\"新建项目\"创建第一个项目。创建后即可分配客户和团队成员。",
  "msg.noProjectsClient": "您尚未被加入任何项目。项目经理将很快邀请您。",
  "msg.saving": "正在保存…",
  "msg.sending": "正在发送…",
  "msg.uploading": "正在上传…",
  "msg.success": "成功",
  "msg.error": "出现错误",
  "proj.count.one": "{n} 个项目",
  "proj.count.other": "{n} 个项目",
  "hero.target": "目标",
  "hero.start": "开始",
  "hero.client": "客户",
  "hero.location": "位置",
  "hero.consultant": "顾问",
  "hero.pm": "项目经理",
  "hero.siteEngineer": "现场工程师",
  "hero.type": "类型",
  "hero.code": "代码",
  "hero.totalProgress": "总进度",
  "hero.activePhase": "当前阶段",
  "hero.programmeNotLoaded": "尚未加载进度计划",
  "hero.complete": "完成",
  "hero.uploadGantt": "上传甘特图以开始",
  "hero.noMilestoneSet": "尚未设置里程碑",
  "hero.targetCompletion": "目标完成日期",
  "hero.notSet": "未设置",
  "hero.unassigned": "未分配",
  "hero.assignInTeam": "在团队与权限中分配",
  "hero.teamMembers": "团队成员",
  "hero.onProject": "{n} 人在项目中",
  "hero.includingClientSubs": "包含客户和分包商",
  "hero.latestUpdate": "最新现场更新",
  "hero.overallProgress": "总进度",
  "hero.projectPhases": "项目阶段",
  "hero.projectWorkstreams": "项目工作流",
  "hero.primarySitePhase": "主要现场阶段",
  "hero.nextOnSite": "下一项现场工作",
  "hero.statusByWorkstream": "按工作流查看项目状态",
  "hero.workstreamsNote": "审批、采购与现场施工并行推进 — 采购不会阻碍现场工作。",
  "hero.stage.one": "{n} 阶段",
  "hero.stage.other": "{n} 阶段",
  "hero.daysRemaining": "剩余 {n} 天",
  "hero.pendingClientActions": "{n} 项客户待办",
  "hero.riskAlerts": "风险提示",
  "hero.keyDocuments": "关键文档",
  "hero.openFiles": "打开文件",
  "hero.quickAccess": "快速访问",
  "daily.title": "每日更新",
  "daily.empty": "暂无每日更新。",
  "daily.workCompleted": "已完成工作",
  "daily.delays": "延误 / 问题",
  "daily.nextPlan": "次日计划",
  "daily.manpower": "人力",
  "daily.materials": "已交付材料",
  "daily.weather": "天气",
  "daily.visibility": "可见范围",
  "daily.linkOptional": "关联到进度活动（可选）",
  "daily.phaseLabel": "阶段",
  "daily.taskLabel": "甘特任务",
  "daily.milestoneLabel": "里程碑",
  "daily.subcontractor": "分包商范围",
  "daily.taskStatus": "更新任务状态",
  "daily.taskProgress": "任务进度 %",
  "daily.milestoneStatus": "更新里程碑状态",
  "daily.milestoneProgress": "里程碑进度 %",
  "daily.keepAsIs": "— 保持不变 —",
  "daily.none": "— 无 —",
  "daily.translateThis": "翻译此条更新",
  "photo.title": "照片",
  "photo.empty": "暂未上传照片。",
  "photo.caption": "说明",
  "photo.addCaption": "添加说明…",
  "photo.dropHint": "将照片拖到此处或点击选择",
  "photo.maxSize": "每张照片最多 25MB · 自动压缩",
  "rep.title": "报告",
  "rep.empty": "尚未生成报告。",
  "rep.generate": "生成报告",
  "rep.generateDialog.title": "生成 {kind} 报告",
  "rep.kind.daily": "每日",
  "rep.kind.weekly": "每周",
  "rep.audience": "受众",
  "rep.audience.internal": "内部（Time Furniture 团队）",
  "rep.audience.client": "客户",
  "rep.includeFinancials": "包含财务（仅管理员）",
  "rep.reportLanguage": "报告语言",
  "rep.langAuto": "自动 · 与收件人的首选语言一致（推荐用于客户邮件）",
  "rep.langHint": "标题、状态标签和章节名称会被翻译。用户输入的内容（工作记录、评论、文件名）保持原样。",
  "rep.recipients": "收件人",
  "rep.recipientsHint": "用逗号分隔的电子邮件地址",
  "rep.draftStatus": "草稿",
  "rep.sentStatus": "已发送",
  "rep.discardConfirm": "丢弃此报告草稿？",
  "file.title": "文件",
  "file.empty": "暂未上传文件。",
  "file.uploadHint": "将文件拖到此处或点击选择",
  "file.maxSize": "每个文件最多 100MB",
  "file.markFinal": "标记为最终版",
  "file.unmarkFinal": "取消最终版标记",
  "file.keyDocs": "最终文件与关键文档",
  "file.keyDocs.empty": "尚未有文档被标记为最终版。",
  "file.deleteConfirm": "确定要删除此文件吗？",
  "mile.title": "里程碑",
  "mile.empty": "暂未定义里程碑。",
  "mile.add": "添加里程碑",
  "mile.next": "下一个里程碑",
  "mile.setNext": "设为下一个",
  "mile.target": "目标 {date}",
  "mile.statusPlanned": "计划中",
  "mile.statusInProgress": "进行中",
  "mile.statusCompleted": "已完成",
  "mile.statusDelayed": "延误",
  "mile.statusBlocked": "受阻",
  "mile.showUpdates": "显示现场更新",
  "mile.hideUpdates": "隐藏现场更新",
  "mile.noUpdatesLinked": "尚无关联到此里程碑的现场更新。",
  "mile.openDetails": "打开",
  "mile.viewAll": "查看全部里程碑",
  "mile.backToSummary": "返回摘要",
  "mile.search": "搜索里程碑…",
  "mile.responsible": "负责方",
  "mile.dependency": "依赖方",
  "mile.linkedUpdates": "关联的现场更新",
  "mile.sec.currentNext": "当前及下一个里程碑",
  "mile.sec.currentEmpty": "尚未设置当前里程碑。请设置一个以突出本周重点。",
  "mile.sec.thisWeek": "本周到期",
  "mile.sec.thisWeekEmpty": "本周没有到期的里程碑。",
  "mile.sec.delayed": "延误或高风险",
  "mile.sec.delayedEmpty": "没有延误或受阻的里程碑 — 一切正常。",
  "mile.filter.upcoming": "即将",
  "mile.filter.thisWeek": "本周",
  "mile.filter.nextWeek": "下周",
  "nav.all": "全部",
  "fin.title": "财务（管理员）",
  "fin.lockedClient": "财务信息仅管理员可见，按项目逐一共享。",
  "fin.import": "导入财务 Excel",
  "fin.totalValue": "项目总价值",
  "fin.byStage": "按阶段细分",
  "fin.paymentMilestones": "付款里程碑",
  "form.required": "必填",
  "form.optional": "可选",
  "form.email": "电子邮箱",
  "form.name": "姓名",
  "form.role": "角色",
  "form.company": "公司",
  "form.phone": "电话",
  "form.notes": "备注",
  "form.description": "描述",
  "form.startDate": "开始日期",
  "form.targetDate": "目标完成日期",
  "form.password": "密码",
  "form.newPassword": "新密码",
  "form.confirmPassword": "确认密码",
  "form.preferredLanguage": "首选语言",
  "form.preferredLanguageHint": "用于该用户的门户标签及邀请/报告邮件。",
  "form.projectName": "项目名称",
  "form.projectCode": "项目代码",
  "form.projectLocation": "位置",
  "form.projectClient": "客户",
  "form.defaultLanguage": "项目默认语言",
  "comment.title": "评论",
  "comment.empty": "暂无评论。",
  "comment.placeholder": "撰写评论…",
  "comment.post": "发布评论",
  "comment.replyPlaceholder": "撰写回复…",
  "msg.confirmDelete": "确定要删除吗？",
  "msg.changesSaved": "更改已保存",
  "msg.languageChanged": "语言已切换为 {lang}",
};

// Merge the additional translations into the base language objects.
Object.assign(en, _moreEN);
Object.assign(ko, _moreKO);
Object.assign(ar, _moreAR);
Object.assign(zh, _moreZH);

export const PORTAL_TRANSLATIONS = { en, ko, ar, zh };

export function isValidLang(code) {
  return LANG_CODES.includes(code);
}
